import { FC, JSX, useEffect, useState } from "react";
import moment, { Moment } from "moment";
import { ColumnConfig, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { ModalLink } from "@app/shared/Modals";
import { FlyoutLink } from "@app/shared/Flyouts";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import AddProductPanelContent from "@app/admin/client-license/AddProductPanelContent";
import AddRecipientPanelContent from "@app/admin/client-license/AddRecipientPanelContent";
import { ClientLicenseOverview, SoftwareLicense, SoftwareLicenseDetail, SoftwareLicenseUserDetail, AddSoftwareLicensedProducts, AddSoftwareLicenseUsers } from "@app/admin/client-license/clientLicenseModels";
import ClientLicenseService from "@app/admin/client-license/clientLicenseService";


interface LicenseTableData {
  [wafflesKeyColumnName]: string;
  licenseId: string;
  clientName: JSX.Element;
  salesforceContractId: string;
  removeBtn: JSX.Element;
}

const licenseColumnConfig: ColumnConfig = {
  clientName: {
    displayText: "Client Name",
    noResize: true,
    noSort: true,
    testId: "client-name",
  },
  licenseId: {
    displayText: "License ID",
    noResize: true,
    noSort: true,
    testId: "license-id",
  },
  removeBtn: {
    alignment: TextAlignment.Right,
    displayText: "",
    noResize: true,
    noSort: true,
    testId: "remove",
  },
  salesforceContractId: {
    displayText: "Salesforce Contract",
    noResize: true,
    noSort: true,
    testId: "salesforce-contract-id",
  },
};

interface ProductTableData {
  [wafflesKeyColumnName]: string;
  productName: string;
  moduleName: string;
  copies: number;
  licensedState: string;
  activationDate: string;
  evalExpDate: string;
  removeBtn: JSX.Element;
}

const productColumnConfig: ColumnConfig = {
  activationDate: {
    displayText: "Activated",
    noResize: true,
    noSort: true,
    testId: "activation-date",
  },
  copies: {
    displayText: "Copies",
    noResize: true,
    noSort: true,
    testId: "copies",
  },
  evalExpDate: {
    displayText: "Expiration Date",
    noResize: true,
    noSort: true,
    testId: "expiration-date",
  },
  licensedState: {
    displayText: "Licensed States",
    noResize: true,
    noSort: true,
    testId: "licensed-state",
  },
  moduleName: {
    displayText: "Modules",
    noResize: true,
    noSort: true,
    testId: "module-name",
  },
  productName: {
    displayText: "Licensed Products",
    noResize: true,
    noSort: true,
    testId: "product-name",
  },
  removeBtn: {
    alignment: TextAlignment.Right,
    displayText: "",
    noResize: true,
    noSort: true,
    testId: "remove",
  },
};

interface UserTableData {
  [wafflesKeyColumnName]: string;
  canDownload: JSX.Element;
  email: string;
  address: JSX.Element;
  phoneNum: string;
  removeBtn: JSX.Element;
}

const userColumnConfig: ColumnConfig = {
  address: {
    displayText: "Address",
    noResize: true,
    noSort: true,
    testId: "address",
  },
  canDownload: {
    displayText: "Can Download",
    noResize: true,
    noSort: true,
    sizeContentToFit: true,
    testId: "can-download",
  },
  email: {
    displayText: "Email",
    noResize: true,
    noSort: true,
    testId: "email",
  },
  phoneNum: {
    displayText: "Phone",
    noResize: true,
    noSort: true,
    testId: "phone-num",
  },
  removeBtn: {
    alignment: TextAlignment.Right,
    displayText: "",
    noResize: true,
    noSort: true,
    testId: "remove-btn",
  },
};

interface ClientLicenseDetailProps {
  clientName: string;
  licenseOverview: ClientLicenseOverview;
  onClientLicenseDetailChanged: () => void;
}

export const ClientLicenseDetail: FC<React.PropsWithChildren<ClientLicenseDetailProps>> = (props: ClientLicenseDetailProps) => {

  const clientLicenseService = new ClientLicenseService();

  const [licenseInfo, setLicenseInfo] = useState<LicenseTableData[]>([]);
  const [productInfo, setProductInfo] = useState<ProductTableData[]>([]);
  const [userInfo, setUserInfo] = useState<UserTableData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    createLicenseInfoWafflesTable(props.licenseOverview.softwareLicense);
    createProductInfoWafflesTable(props.licenseOverview.licensedProducts);
    createUserInfoWafflesTable(props.licenseOverview.softwareLicense, props.licenseOverview.licensedUsers);
  }, [props.licenseOverview]);

  const createLicenseInfoWafflesTable = (licenseInfo: SoftwareLicense) => {
    const license: LicenseTableData[] = [];

    if (!!licenseInfo) {
      /* oxlint-disable sort-keys */
      license.push({
        [wafflesKeyColumnName]: licenseInfo.licenseId.toString(),
        licenseId: licenseInfo.licenseId.toString(),
        clientName: (!!licenseInfo.sfAccountId ?
          <a href={`https://na4.salesforce.com/${licenseInfo.sfAccountId}`} target="SFWindow">{props.clientName}</a>
          : <>{props.clientName}</>
        ),
        salesforceContractId: licenseInfo.sfContractId?.toString(),
        removeBtn:
          <ModalLink
            content={{
              body: licenseInfo.licenseId.toString(),
              title: "Delete License",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => handleLicenseTerminate(licenseInfo.licenseId),
              confirmButtonText: "Delete",
              confirmButtonType: "primary-danger",
            }}
            testId="client-license-detail__delete-modal"
            classNames="qes-button-secondary danger"
            hideBodyScroll={true}
          >
            Delete
          </ModalLink>
      });
      /* oxlint-enable sort-keys */
    }

    setLicenseInfo(license);
  }

  const createProductInfoWafflesTable = (licensedProducts: SoftwareLicenseDetail[]) => {
    const products: ProductTableData[] = []

    if (!!licensedProducts) {
      for (const product of licensedProducts) {

        //Skip if the SoftwareLicenseDetail doesn't contain any valid product information
        if (product.productId === 0)
          continue;

        /* oxlint-disable sort-keys */
        products.push({
          [wafflesKeyColumnName]: product.productId.toString(),
          productName: `${product.productSuiteName} (${product.productEditionName})`,
          moduleName: product.productModuleName,
          copies: product.numCopies,
          licensedState: (product.isNational?.toLowerCase() === "y" ? "National" : product.licensedStates),
          activationDate: product.dateActivated ? moment.utc(product.dateActivated).local().format("MMM DD, YYYY h:mm:ss A") : null,
          evalExpDate: !!product.evalExpirationDate ? moment(product.evalExpirationDate).format("MMM DD, YYYY") : null,
          removeBtn:
            <ModalLink
              content={{
                body: product.productName,
                title: "Delete Product License",
                cancelButtonText: "Cancel",
                confirmButtonAction: () => handleLicensedProductTerminate(product.licenseId, product.licensedProductId),
                confirmButtonText: "Delete",
                confirmButtonType: "primary-danger",
              }}
              testId={`client-license-detail__product-delete-modal--${product.productId}`}
              classNames="qes-button-secondary danger"
              hideBodyScroll={true}
            >
              Delete
            </ModalLink>
        });
        /* oxlint-enable sort-keys */
      }
    }

    setProductInfo(products);
  }

  const createUserInfoWafflesTable = (licenseInfo: SoftwareLicense, licenseUsers: SoftwareLicenseUserDetail[]) => {
    const users: UserTableData[] = []

    if (!!licenseInfo && !!licenseUsers) {
      for (const licUser of licenseUsers) {
        /* oxlint-disable sort-keys */
        users.push({
          [wafflesKeyColumnName]: licUser.userId.toString(),
          canDownload: (
            <QESCheckbox
              label=""
              testId={`client-license-detail__can-download`}
              checked={!!licUser.canDownload}
              onChange={(e) => {
                handleLicenseUserCanDownloadChange(licUser.licenseId, licUser.softwareLicenseUserId, e.target.checked);
              }} />
          ),
          email: licUser.email,
          address:
            <>
              {licUser.address}<br />
              {`${licUser.city}, ${licUser.state} ${licUser.zip}`}
            </>,
          phoneNum: licUser.phoneNumber,
          removeBtn:
            <ModalLink
              content={{
                body: licUser.email,
                title: "Delete User",
                cancelButtonText: "Cancel",
                confirmButtonAction: () => handleLicenseUserTerminate(licUser.licenseId, licUser.softwareLicenseUserId),
                confirmButtonText: "Delete",
                confirmButtonType: "primary-danger",
              }}
              testId={`client-license-detail__user-delete-modal--${licUser.userId}`}
              classNames="qes-button-secondary danger"
              hideBodyScroll={true}
            >
              Delete
            </ModalLink>
        });
        /* oxlint-enable sort-keys */
      }
    }

    setUserInfo(users);
  }

  const handleLicenseTerminate = async (licenseId: number) => {
    setLoading(true);
    await clientLicenseService.terminateSoftareLicense(licenseId);
    props.onClientLicenseDetailChanged();
    setLoading(false);
  }

  const handleLicensedProductTerminate = async (licenseId: number, licensedProductId: number) => {
    setLoading(true);
    await clientLicenseService.terminateSoftwareLicensedProduct(licenseId, licensedProductId);
    props.onClientLicenseDetailChanged();
    setLoading(false);
  }

  const handleLicenseUserTerminate = async (licenseId: number, licenseUserId: number) => {
    setLoading(true);
    await clientLicenseService.terminateSoftwareLicenseUser(licenseId, licenseUserId);
    props.onClientLicenseDetailChanged();
    setLoading(false);
  }

  const handleLicenseUserCanDownloadChange = async (licenseId: number, licenseUserId: number, canDownload: boolean) => {
    setLoading(true);
    await clientLicenseService.setCanDownload(licenseId, licenseUserId, canDownload);
    props.onClientLicenseDetailChanged();
    setLoading(false);
  }

  const handleAddProducts = async (productIds: string[], numCopies: string, isEvaluation: boolean, isNational: boolean, licensedStates: string[], evalExpDate?: Moment) => {
    setLoading(true);

    const addProductsModel: AddSoftwareLicensedProducts = {
      evaluationExpirationDate: evalExpDate,
      isEvaluation,
      isNational,
      licensedStates,
      numCopies: parseInt(numCopies),
      productIds: productIds.map(x => parseInt(x)),
    }
    await clientLicenseService.addSoftwareLicensedProducts(props.licenseOverview.softwareLicense.licenseId, addProductsModel)
    props.onClientLicenseDetailChanged();
    setLoading(false);
  }

  const handleAddRecipients = async (userIds: string[]) => {
    setLoading(true);

    const addUsersModel: AddSoftwareLicenseUsers = {
      canDownload: false,
      userIdsToAdd: userIds.map(x => parseInt(x))
    }
    await clientLicenseService.addSoftwareLicenseUsers(props.licenseOverview.softwareLicense.licenseId, addUsersModel)
    props.onClientLicenseDetailChanged();
    setLoading(false);
  }

  return (
    <>
      {loading ?
        <LoadingSpinner /> :
        <>
          <h5 className="fw-normal">License</h5>
          <Waffles<LicenseTableData>
            columns={licenseColumnConfig}
            data={licenseInfo}
            fullWidth={true}
            testId="client-license-detail__license-grid-waffles-table"
          />
          <h5 className="fw-normal mt-3">Products</h5>
          <Waffles<ProductTableData>
            columns={productColumnConfig}
            data={productInfo}
            fullWidth={true}
            testId="client-license-detail__product-grid-waffles-table"
          />
          {props.licenseOverview.unLicensedProducts.length > 0 ?
            <FlyoutLink
              classNames="qes-button-primary mt-2"
              content={{
                body: <AddProductPanelContent
                      licenseId={props.licenseOverview.softwareLicense.licenseId}
                      licensedProducts={props.licenseOverview.licensedProducts}
                      unlicensedProducts={props.licenseOverview.unLicensedProducts}
                      onAddProducts={handleAddProducts} />,
                title: "Update a Product",
              }}
              testId="client-license-detail__add-product-flyout"
            >
              <i className="fa fa-user-plus me-1" />Update Product Licenses
            </FlyoutLink>
            : null
          }
          <h5 className="fw-normal mt-3">Users</h5>
          <Waffles<UserTableData>
            columns={userColumnConfig}
            data={userInfo}
            fullWidth={true}
            testId="client-license-detail__user-grid-waffles-table"
          />
          {props.licenseOverview.potentialUsers.length > 0 ?
            <FlyoutLink
              classNames="qes-button-primary mt-2"
              content={{
                body: <AddRecipientPanelContent
                  licenseId={props.licenseOverview.softwareLicense.licenseId}
                  potentialUsers={props.licenseOverview.potentialUsers}
                  onAddRecipients={handleAddRecipients} />,
                title: "Add a License User",
              }}
              testId="client-license-detail__add-recipient-flyout"
              size="40"
            >
              <i className="fa fa-user-plus me-1" />Add Recipient
            </FlyoutLink>
            : null
          }
        </>
      }
    </>
  );
}